import { Controller } from '@hotwired/stimulus'
import { format, toZonedTime, fromZonedTime } from 'date-fns-tz'

// The shared filters-bar date picker. Presets are relative tokens (last_2d, today, …): clicking
// one submits ?created_at=<token>, which RelativeDateRange re-resolves server-side every request,
// so a bookmarked preset URL stays a "live" sliding window. Only the custom calendar range freezes
// an explicit created_at_start/created_at_end window.
export default class extends Controller {
    static targets = ['startInput', 'endInput', 'tokenInput', 'picker']
    static values = {
        userIanaTimezone: String,
        submitOnApply: { type: Boolean, default: false },
        // Ordered [{ token, label }] presets, rendered server-side from RelativeDateRange so the
        // labels can never drift from the grammar.
        presets: Array,
        // Label of the active relative token ("Past 2 days"), shown in the input and highlighted
        // in the preset list; blank for a frozen custom range.
        activeLabel: String,
    }

    connect() {
        // jquery+daterangepicker are both global until we switch pipeline off sprockets
        this.dateRangePicker = $(this.pickerTarget)
            .daterangepicker(
                {
                    startDate: this.#convertToTimeZone(
                        this.startInputTarget.value,
                        this.userIanaTimezoneValue,
                    ),
                    endDate: this.#convertToTimeZone(
                        this.endInputTarget.value,
                        this.userIanaTimezoneValue,
                    ),
                    timePicker: true,
                    ranges: this.#presetRanges(),
                    autoUpdateInput: true,
                    cancelButtonClasses: 'btn-secondary',
                    applyButtonClasses: 'btn-success',
                    showDropdowns: true,
                    autoApply: true,
                    locale: {
                        format: 'YYYY-MM-DD hh:mm A',
                    },
                },
                (startDate, endDate, label) => {
                    const token = this.#tokenForLabel(label)

                    if (token) {
                        // Live preset: carry the token, drop the frozen window so the token wins.
                        this.tokenInputTarget.disabled = false
                        this.tokenInputTarget.value = token
                        this.startInputTarget.disabled = true
                        this.endInputTarget.disabled = true
                    } else {
                        // Custom range: freeze an explicit window, drop any active token.
                        this.tokenInputTarget.disabled = true
                        this.startInputTarget.disabled = false
                        this.endInputTarget.disabled = false
                        this.startInputTarget.value = this.#convertToTimeZoneISO8601(
                            startDate._d,
                            this.userIanaTimezoneValue,
                        )
                        this.endInputTarget.value = this.#convertToTimeZoneISO8601(
                            endDate._d,
                            this.userIanaTimezoneValue,
                        )
                    }

                    if (this.submitOnApplyValue) {
                        this.element.closest('form').requestSubmit()
                    }
                },
            )
            .data('daterangepicker')

        // The server already knows which preset is active — highlight it ourselves rather than
        // relying on daterangepicker matching dates (which would flake on sliding windows).
        $(this.pickerTarget).on('show.daterangepicker', () => this.#highlightActivePreset())
        this.#applyInitialState()
    }

    openCalendar(_event) {
        this.pickerTarget.focus()
    }

    dontSubmitForm(event) {
        event.preventDefault()
    }

    // private

    // Initial render reflects what the server resolved: when a token is active show its label and
    // keep the frozen window out of the form; otherwise leave the explicit window in place.
    #applyInitialState() {
        const tokenActive = this.tokenInputTarget.value !== ''

        this.startInputTarget.disabled = tokenActive
        this.endInputTarget.disabled = tokenActive
        this.tokenInputTarget.disabled = !tokenActive

        if (tokenActive && this.activeLabelValue) {
            this.pickerTarget.value = this.activeLabelValue
        }
    }

    // Cosmetic [start, end] per preset so daterangepicker can render the buttons. Every preset
    // gets the server-resolved window — clicks navigate by token, so the exact dates never matter.
    #presetRanges() {
        const start = this.startInputTarget.value
        const end = this.endInputTarget.value
        return Object.fromEntries(
            this.presetsValue.map(({ label }) => [label, [moment(start), moment(end)]]),
        )
    }

    #highlightActivePreset() {
        const items = this.dateRangePicker.container.find('.ranges li')
        items.removeClass('active')
        if (this.activeLabelValue) {
            items.filter(`[data-range-key="${this.activeLabelValue}"]`).addClass('active')
        }
    }

    #tokenForLabel(label) {
        return this.presetsValue.find((preset) => preset.label === label)?.token
    }

    #convertToTimeZoneISO8601(date, timeZone) {
        const utcDate = fromZonedTime(date, timeZone)

        // 2025-02-24T05:02:00.000Z -> 2025-02-24T05:02:00Z
        return `${utcDate.toISOString().slice(0, 19)}Z`
    }

    #convertToTimeZone(iso8601Date, timeZone) {
        const date = toZonedTime(new Date(iso8601Date), timeZone)
        return format(date, 'yyyy-MM-dd hh:mm a')
    }
}
